import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import CellAdditionalServiceActions from 'src/features/parkingManagement/components/AdditionalServices/CellAdditionalServiceActions.tsx';
import {
    ADDITIONAL_SERVICES_TYPE,
    ADDITIONAL_SERVICES_USED_FOR,
    type AdditionalServices,
} from 'src/features/parkingManagement/types/additionalServices.ts';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils/messages.ts';
import { reservationMessages } from 'src/features/reservations/utils/messages.ts';
import {
    CellDatetime,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
} from 'src/registry/components/table';
import { Badge } from 'src/registry/ui/Badge.tsx';
import { generalMessages } from 'src/utils/messages.ts';

const columnHelper = createColumnHelper<AdditionalServices>();

export const AdditionalServicesTableColumns = ({
    openDeleteAdditionalServiceModal,
    openDeactivateAdditionalServiceModal,
    openActivateAdditionalServiceModal,
    openEditAdditionalServiceModal,
}: {
    openDeleteAdditionalServiceModal: (service: AdditionalServices) => void;
    openDeactivateAdditionalServiceModal: (service: AdditionalServices) => void;
    openActivateAdditionalServiceModal: (service: AdditionalServices) => void;
    openEditAdditionalServiceModal: (service: AdditionalServices) => void;
}) => {
    const { formatMessage, formatNumber } = useIntl();

    return [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const isServiceCharge = row.original.type === ADDITIONAL_SERVICES_TYPE.SERVICE_CHARGE;
                return isServiceCharge
                    ? formatMessage(parkingManagementMessages.serviceCharge)
                    : formatMessage(parkingManagementMessages.cancellationOption);
            },
            enableHiding: true,
            meta: {
                title: formatMessage(parkingManagementMessages.type),
            },
        }),
        columnHelper.accessor('used_for', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const badgeLabel =
                    row.original.used_for === ADDITIONAL_SERVICES_USED_FOR.VEHICLE_STAY
                        ? parkingManagementMessages.vehicleStay
                        : parkingManagementMessages[row.original.used_for];
                const isVehicleStay = row.original.used_for === ADDITIONAL_SERVICES_USED_FOR.VEHICLE_STAY;
                const isReservation = row.original.used_for === ADDITIONAL_SERVICES_USED_FOR.RESERVATION;
                const badgeTheme = isVehicleStay ? 'blue' : isReservation ? 'emerald' : 'violet';
                return <Badge theme={badgeTheme}>{formatMessage(badgeLabel)}</Badge>;
            },
            enableHiding: true,
            meta: {
                title: formatMessage(parkingManagementMessages.module),
            },
        }),
        columnHelper.accessor('price_with_tax', {
            header: DataTableColumnHeader,
            cell: ({ row }) =>
                formatNumber(row.original.price_with_tax / 100, {
                    style: 'currency',
                    currency: row.original.currency,
                }),
            enableHiding: true,
            meta: {
                title: formatMessage(reservationMessages.price),
            },
        }),
        columnHelper.accessor('is_optional', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const isOptional = row.original.is_optional;
                const badgeTheme = isOptional ? 'emerald' : 'red';
                const badgeText = isOptional ? formatMessage(generalMessages.yes) : formatMessage(generalMessages.no);
                return <Badge theme={badgeTheme}>{badgeText}</Badge>;
            },
            enableHiding: true,
            meta: {
                title: formatMessage(parkingManagementMessages.optional),
            },
        }),
        columnHelper.accessor('is_enabled', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const isEnabled = row.original.is_enabled;
                const badgeTheme = isEnabled ? 'emerald' : 'red';
                const badgeText = isEnabled
                    ? formatMessage(parkingManagementMessages.activeService)
                    : formatMessage(parkingManagementMessages.inactiveService);
                const badgeIcon = isEnabled ? faCheckCircle : faXmarkCircle;

                return (
                    <Badge theme={badgeTheme}>
                        <FontAwesomeIcon icon={badgeIcon} />
                        {badgeText}
                    </Badge>
                );
            },
            enableHiding: true,
            meta: {
                title: formatMessage(parkingManagementMessages.activeService),
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            meta: {
                title: formatMessage(reservationMessages.reservationValidFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            meta: {
                title: formatMessage(reservationMessages.reservationValidTo),
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => (
                <CellAdditionalServiceActions
                    deleteCallback={openDeleteAdditionalServiceModal}
                    editCallback={openEditAdditionalServiceModal}
                    deactivateCallback={openDeactivateAdditionalServiceModal}
                    activateCallback={openActivateAdditionalServiceModal}
                    {...props}
                />
            ),
        }),
    ];
};
